import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { api, isApiConfigured } from '@/lib/api'
import { getCurrentUserId } from '@/lib/sync'
import { EMOTE_CATALOG, getEmoteById } from '@/lib/emotes'
import { showNotification, NotificationTemplates } from '@/lib/notifications'
import { AFFECTION_GAINS } from '@/lib/affection'
import { realtimeClient } from '@/lib/websocket'
import { usePartnershipStore } from './usePartnershipStore'
import type { EmoteMessage, EmoteMessageWithDetails, Emote } from '@/types/social'

interface EmoteState {
  emoteMessages: EmoteMessage[]
  isLoading: boolean
  error: string | null

  fetchEmoteMessages: (partnershipId?: string) => Promise<void>
  sendEmote: (partnershipId: string, emoteId: string, message?: string) => Promise<EmoteMessage | null>
  markAsRead: (messageId: string) => Promise<void>
  markAllAsRead: (partnershipId: string) => Promise<void>
  getUnreadCount: (partnershipId?: string) => number
  getMessagesForPartnership: (partnershipId: string) => EmoteMessage[]
  getRecentMessages: (limit?: number) => EmoteMessage[]
  getMessageWithDetails: (messageId: string) => Promise<EmoteMessageWithDetails | null>
  getAvailableEmotes: (isSpicyEnabled: boolean) => Emote[]
  subscribeToEmotes: () => () => void
  reset: () => void
}

function mapEmoteMessage(m: Record<string, unknown>): EmoteMessage {
  return {
    id: m.id as string,
    partnershipId: (m.partnership_id ?? m.partnershipId) as string,
    senderId: (m.sender_id ?? m.senderId) as string,
    receiverId: (m.receiver_id ?? m.receiverId) as string,
    emoteId: (m.emote_id ?? m.emoteId) as string,
    message: (m.message ?? null) as string | null,
    isSpicy: Boolean(m.is_spicy ?? m.isSpicy),
    readAt: (m.read_at ?? m.readAt ?? null) as string | null,
    notificationSent: Boolean(m.notification_sent ?? m.notificationSent),
    notificationSentAt: (m.notification_sent_at ?? m.notificationSentAt ?? null) as string | null,
    createdAt: (m.created_at ?? m.createdAt) as string,
  }
}

export const useEmoteStore = create<EmoteState>()(
  persist(
    (set, get) => ({
      emoteMessages: [],
      isLoading: false,
      error: null,

      fetchEmoteMessages: async (partnershipId) => {
        if (!isApiConfigured()) return
        const userId = getCurrentUserId()
        if (userId === 'local') return

        set({ isLoading: true })
        try {
          const params = partnershipId ? `?partnership_id=${partnershipId}` : ''
          const data = await api.get<Record<string, unknown>[]>(`/api/emotes${params}`)
          set({ emoteMessages: data.map(mapEmoteMessage), isLoading: false })
        } catch (error) {
          console.error('[Emote] Failed to fetch:', error)
          set({ isLoading: false })
        }
      },

      sendEmote: async (partnershipId, emoteId, message) => {
        if (!isApiConfigured()) { set({ error: 'API not configured' }); return null }
        const userId = getCurrentUserId()
        if (userId === 'local') { set({ error: 'Must be logged in' }); return null }

        const emote = getEmoteById(emoteId)
        if (!emote) { set({ error: 'Invalid emote' }); return null }

        set({ isLoading: true, error: null })
        try {
          // Get receiver ID from partnership
          const partnership = usePartnershipStore.getState().partnerships.find(p => p.id === partnershipId)
          if (!partnership) { set({ error: 'Partnership not found', isLoading: false }); return null }

          const receiverId = partnership.userAId === userId ? partnership.userBId : partnership.userAId

          const data = await api.post<{ id: string }>('/api/emotes', {
            partnership_id: partnershipId,
            receiver_id: receiverId,
            emote_id: emoteId,
            message: message || null,
            is_spicy: emote.isSpicy,
          })

          const emoteMessage: EmoteMessage = {
            id: data.id,
            partnershipId,
            senderId: userId,
            receiverId,
            emoteId,
            message: message ?? null,
            isSpicy: emote.isSpicy,
            readAt: null,
            notificationSent: false,
            notificationSentAt: null,
            createdAt: new Date().toISOString(),
          }

          set((state) => ({
            emoteMessages: [emoteMessage, ...state.emoteMessages],
            isLoading: false,
          }))

          // Add affection
          const affectionAmount = emote.isSpicy
            ? AFFECTION_GAINS.spicyEmoteSent
            : AFFECTION_GAINS.emoteSent
          usePartnershipStore.getState().addAffection(partnershipId, affectionAmount)

          return emoteMessage
        } catch (error) {
          console.error('[Emote] Failed to send:', error)
          set({ error: 'Failed to send emote', isLoading: false })
          return null
        }
      },

      markAsRead: async (messageId) => {
        if (!isApiConfigured()) return
        const userId = getCurrentUserId()
        const message = get().emoteMessages.find((m) => m.id === messageId)
        if (!message || message.receiverId !== userId || message.readAt) return

        try {
          await api.patch(`/api/emotes/${messageId}/read`)
          set((state) => ({
            emoteMessages: state.emoteMessages.map((m) =>
              m.id === messageId ? { ...m, readAt: new Date().toISOString() } : m
            ),
          }))
        } catch (error) {
          console.error('[Emote] Failed to mark as read:', error)
        }
      },

      markAllAsRead: async (partnershipId) => {
        if (!isApiConfigured()) return
        try {
          await api.post('/api/emotes/read-all', { partnership_id: partnershipId })
          const userId = getCurrentUserId()
          set((state) => ({
            emoteMessages: state.emoteMessages.map((m) =>
              m.partnershipId === partnershipId && m.receiverId === userId && !m.readAt
                ? { ...m, readAt: new Date().toISOString() }
                : m
            ),
          }))
        } catch (error) {
          console.error('[Emote] Failed to mark all as read:', error)
        }
      },

      getUnreadCount: (partnershipId) => {
        const userId = getCurrentUserId()
        return get().emoteMessages.filter(
          (m) =>
            m.receiverId === userId &&
            !m.readAt &&
            (!partnershipId || m.partnershipId === partnershipId)
        ).length
      },

      getMessagesForPartnership: (partnershipId) => {
        return get().emoteMessages
          .filter((m) => m.partnershipId === partnershipId)
          .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
      },

      getRecentMessages: (limit = 20) => get().emoteMessages.slice(0, limit),

      getMessageWithDetails: async (messageId) => {
        const message = get().emoteMessages.find((m) => m.id === messageId)
        if (!message) return null

        const emote = getEmoteById(message.emoteId)
        if (!emote) return null

        const userId = getCurrentUserId()
        return {
          ...message,
          emote,
          senderUsername: null,
          senderDisplayName: null,
          isSentByMe: message.senderId === userId,
        }
      },

      getAvailableEmotes: (isSpicyEnabled) => {
        if (isSpicyEnabled) return EMOTE_CATALOG
        return EMOTE_CATALOG.filter((e) => !e.isSpicy)
      },

      subscribeToEmotes: () => {
        if (!isApiConfigured()) return () => {}

        const unsub = realtimeClient.on('emote_received', (msg) => {
          const data = msg.data as Record<string, unknown>
          const message = mapEmoteMessage(data)

          set((state) => ({
            emoteMessages: [message, ...state.emoteMessages],
          }))

          // Add affection for receiving
          const emoteData = getEmoteById(message.emoteId)
          const affectionAmount = emoteData?.isSpicy
            ? AFFECTION_GAINS.spicyEmoteReceived
            : AFFECTION_GAINS.emoteReceived
          usePartnershipStore.getState().addAffection(message.partnershipId, affectionAmount)

          // Show browser notification if app is in background
          if (document.hidden && emoteData) {
            const notif = NotificationTemplates.emoteReceived('Your partner', emoteData.name, emoteData.emoji)
            showNotification(notif.title, {
              body: notif.body,
              tag: notif.tag,
              onClick: () => { window.location.href = '/partners' },
            })
          }
        })

        return unsub
      },

      reset: () => {
        set({ emoteMessages: [], isLoading: false, error: null })
      },
    }),
    {
      name: 'waifudo-emotes',
      partialize: (state) => ({
        emoteMessages: state.emoteMessages.slice(0, 50),
      }),
    }
  )
)
